/**
 * GoobDoob VTT — party-scoped shared state.
 *
 * Covers three tightly-related concepts:
 *
 *   1. Party loot  — shared items not yet distributed to a character.
 *   2. Wallet      — pooled currency in copper / silver / gold / platinum.
 *   3. Budget pools — named earmarks on top of the wallet. A pool can be
 *                     scoped to the whole party ("rations fund") or to a
 *                     single character ("Saru's pocket money").
 *
 * Every mutation on any of the above writes a LogEntry to the session log.
 * The log is the audit trail and the safety net for the trust model — any
 * player can spend from a party pool, any player can withdraw to themselves,
 * so visibility is how the table stays honest.
 *
 * Single-table DDB layout:
 *
 *     PK                     SK
 *     SESSION#<sessionId>    PARTY                  <- Party (this file, whole doc)
 *     SESSION#<sessionId>    LOOT#<itemId>          <- LootItem (one per row)
 *     SESSION#<sessionId>    LOG#<iso-ts>#<rand>    <- LogEntry (append-only)
 *
 * Party is a single small document — wallet + pools. Loot is one row per
 * item so large lists don't rewrite the whole party doc. Log is append-only
 * with ISO timestamp sort keys for natural time-ordered queries.
 *
 * Server-side mirror: GoobDoobVtt.Domain.Party. Keep in lockstep.
 */

/**
 * Copper / silver / gold / platinum. All values non-negative integers.
 * Standard 5e exchange: 1pp = 10gp = 100sp = 1000cp.
 */
export interface Wallet {
  cp: number;
  sp: number;
  gp: number;
  pp: number;
}

export const ZERO_WALLET: Wallet = { cp: 0, sp: 0, gp: 0, pp: 0 };

/** Standard 5e exchange rates, expressed in copper per unit. */
export const COIN_IN_COPPER: Record<keyof Wallet, number> = {
  cp: 1,
  sp: 10,
  gp: 100,
  pp: 1000,
};

/** Convert a wallet to a single total in copper for arithmetic. */
export function walletToCopper(w: Wallet): number {
  return w.cp * COIN_IN_COPPER.cp
    + w.sp * COIN_IN_COPPER.sp
    + w.gp * COIN_IN_COPPER.gp
    + w.pp * COIN_IN_COPPER.pp;
}

/**
 * Budget pool — earmarked funds sitting on top of the wallet.
 *
 * - `ownerScope = 'party'` — any player may deposit or spend from it.
 * - `ownerScope = 'char:<characterId>'` — only the owner and DM may spend
 *   from it; anyone can *deposit into* it (e.g. paying someone their share).
 *
 * The ownerScope wire format is a plain string matching the C# convention
 * in GoobDoobVtt.Domain.BudgetPool: either the literal `"party"` or
 * `"char:<characterId>"`. Use the helper functions below to read/write it
 * ergonomically without string-parsing in every call site.
 *
 * `currentAmount` is stored per-coin, not as a copper total. Players spend
 * from the *coins they actually have*, not from an abstract value: a pool
 * holding 10gp and 0sp can't pay a 1sp tavern bill without being converted
 * first. Keeping the breakdown explicit means the spend path can reject
 * "can't make change" situations loudly instead of silently decomposing.
 */
export interface BudgetPool {
  id: string;
  name: string;
  description?: string;
  /** Current balance of the pool, per coin. */
  currentAmount: Wallet;
  /** Optional savings target, per coin; used to render a progress bar. */
  targetAmount?: Wallet;
  /**
   * `"party"` or `"char:<characterId>"`. Use `isPartyScope()`,
   * `characterOwnerId()`, and `charScope()` helpers instead of matching
   * the string directly.
   */
  ownerScope: string;
  /** Archived pools are hidden from the default view but kept for audit. */
  archived?: boolean;
  /** ISO timestamp of creation. */
  createdAt: string;
}

// -------- ownerScope helpers --------

const CHAR_PREFIX = 'char:';

/** True if the pool is party-wide (any player may spend). */
export function isPartyScope(scope: string): boolean {
  return scope === 'party';
}

/**
 * Extract the character id from a char-scoped pool, or null if party-scoped.
 * Matches the C# `BudgetPool.CharacterOwnerId()` helper.
 */
export function characterOwnerId(scope: string): string | null {
  return scope.startsWith(CHAR_PREFIX) ? scope.slice(CHAR_PREFIX.length) : null;
}

/** Build a character-scoped ownerScope string. Matches C# `"char:" + id`. */
export function charScope(characterId: string): string {
  return `${CHAR_PREFIX}${characterId}`;
}

/** Human-readable label for display. */
export function ownerScopeLabel(scope: string, nameMap?: Map<string, string>): string {
  if (isPartyScope(scope)) return 'Party';
  const charId = characterOwnerId(scope);
  if (!charId) return scope; // shouldn't happen, defensive
  const name = nameMap?.get(charId);
  return name ? name : charId;
}

/** Sum two wallets coin-wise. Neither input is mutated. */
export function addWallets(a: Wallet, b: Wallet): Wallet {
  return {
    cp: a.cp + b.cp,
    sp: a.sp + b.sp,
    gp: a.gp + b.gp,
    pp: a.pp + b.pp,
  };
}

/** Subtract `b` from `a` coin-wise. Any coin going negative signals a bug. */
export function subWallets(a: Wallet, b: Wallet): Wallet {
  return {
    cp: a.cp - b.cp,
    sp: a.sp - b.sp,
    gp: a.gp - b.gp,
    pp: a.pp - b.pp,
  };
}

/** True if every coin in `a` is >= the matching coin in `b`. */
export function walletCovers(a: Wallet, b: Wallet): boolean {
  return a.cp >= b.cp && a.sp >= b.sp && a.gp >= b.gp && a.pp >= b.pp;
}

/**
 * The single party document for a session. Wallet + pools. Unallocated funds
 * = walletToCopper(wallet) - sum(pools.currentAmount). Always derived, never
 * stored, so the two can't drift out of sync.
 */
export interface Party {
  sessionId: string;
  /** Monotonic version for optimistic concurrency on WS writes. */
  version: number;
  wallet: Wallet;
  pools: BudgetPool[];
  updatedAt: string;
}

export function emptyParty(sessionId: string): Party {
  return {
    sessionId,
    version: 0,
    wallet: { ...ZERO_WALLET },
    pools: [],
    updatedAt: new Date(0).toISOString(),
  };
}

/**
 * Unallocated funds, per coin: the wallet minus the coin-wise sum of every
 * non-archived pool's `currentAmount`. Any coin going negative means a
 * mutation path has a bug — pool funding should never over-allocate a
 * coin that the wallet doesn't hold.
 */
export function unallocatedWallet(party: Party): Wallet {
  const allocated = party.pools
    .filter(p => !p.archived)
    .reduce((acc, p) => addWallets(acc, p.currentAmount), ZERO_WALLET);
  return subWallets(party.wallet, allocated);
}

/**
 * A single loot row. Stored as its own DDB item keyed
 * SESSION#<sessionId> / LOOT#<itemId> so the loot list can grow without
 * rewriting the whole party doc.
 */
export interface LootItem {
  id: string;
  sessionId: string;
  name: string;
  quantity: number;
  weight?: number;
  notes?: string;
  /** Who or what encounter the item came from. Display + audit. */
  source?: string;
  /** Unidentified items render as "Unknown <type>" until the DM flips this. */
  identified: boolean;
  /** Free-form tags for filtering — 'magic', 'consumable', 'weapon', etc. */
  tags?: string[];
  /** ISO timestamp; used for stable ordering in the UI. */
  addedAt: string;
}

/**
 * Audit log entry. Append-only. Sort-key includes the ISO timestamp plus a
 * short random suffix so two entries generated in the same millisecond don't
 * collide.
 */
export type LogAction =
  | 'loot.add'
  | 'loot.remove'
  | 'loot.transferToCharacter'
  | 'wallet.deposit'
  | 'wallet.withdraw'
  | 'wallet.convert'
  | 'pool.create'
  | 'pool.rename'
  | 'pool.archive'
  | 'pool.fund'
  | 'pool.unfund'
  | 'pool.spend'
  | 'character.hpChange'
  | 'character.conditionChange'
  | 'character.slotChange'
  | 'character.inventoryChange';

/**
 * Typed log target. Discriminated on `kind`; the `id` field shape depends
 * on which kind it is. Stored this way so the log panel can filter/search
 * by target type or by a specific entity id without string parsing.
 *
 * - `party.wallet`        — party-wide wallet. No id.
 * - `party.pool`          — a budget pool.            id = poolId
 * - `party.loot`          — a loot item.              id = lootItemId
 * - `character.hp`        — a character's HP changed. id = characterId
 * - `character.slots`     — spell slot state changed. id = characterId
 * - `character.resources` — class resource (Ki, CD).  id = characterId
 * - `character.conditions`— conditions changed.       id = characterId
 * - `character.inventory` — inventory changed.        id = characterId
 * - `character.deathSaves`— death save state changed. id = characterId
 */
export type LogTarget =
  | { kind: 'party.wallet' }
  | { kind: 'party.pool'; poolId: string }
  | { kind: 'party.loot'; lootItemId: string }
  | { kind: 'character.hp'; characterId: string }
  | { kind: 'character.slots'; characterId: string }
  | { kind: 'character.resources'; characterId: string }
  | { kind: 'character.conditions'; characterId: string }
  | { kind: 'character.inventory'; characterId: string }
  | { kind: 'character.deathSaves'; characterId: string };

export interface LogEntry {
  id: string;
  sessionId: string;
  /** ISO timestamp; same value is embedded in the DDB sort key. */
  timestamp: string;
  /** Cognito sub of the user who performed the action. */
  actorUserId: string;
  /** Display name of the actor, denormalised for cheap reads. */
  actorName: string;
  action: LogAction;
  /** Typed target — see LogTarget for the full list of kinds. */
  target: LogTarget;
  /**
   * Structured delta. Shape depends on action; readers should treat it as
   * opaque JSON and only interpret the keys they know about. Examples:
   *   { cp: -50, gp: 5 }           // wallet.convert
   *   { amount: 120, unit: 'cp' }  // pool.spend
   *   { hp: -12, from: 34, to: 22 } // character.hpChange
   */
  delta: Record<string, unknown>;
  /** Optional human reason the actor supplied. */
  reason?: string;
}
